import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Heart, Trash2, Loader2, Crown } from 'lucide-react';
import api from '@/utils/api';

const FREE_FAVORITES_LIMIT = 3;

export const FavoritesModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnlimited, setHasUnlimited] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadFavorites();
      loadSubscriptionStatus();
    }
  }, [isOpen]);

  const loadFavorites = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/favorites/list');
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setHasUnlimited(Boolean(response.data?.limits?.favorites));
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const removeFavorite = async (barcode) => {
    try {
      await api.delete(`/favorites/remove/${barcode}`);
      setFavorites(favorites.filter(f => f.barcode !== barcode));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const isNearLimit = !hasUnlimited && favorites.length >= FREE_FAVORITES_LIMIT;
  const isApproachingLimit = !hasUnlimited && favorites.length === FREE_FAVORITES_LIMIT - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-[#8FBC8F]" />
            {t('myFavorites')}
            {!hasUnlimited && (
              <span
                data-testid="favorites-counter"
                className={`ml-auto text-sm font-medium ${isNearLimit ? 'text-red-600' : isApproachingLimit ? 'text-amber-600' : 'text-gray-500'}`}
              >
                {favorites.length}/{FREE_FAVORITES_LIMIT}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Free-tier limit banner */}
          {isNearLimit && (
            <div
              data-testid="favorites-limit-banner"
              className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4 flex items-start gap-3"
            >
              <Crown className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900 text-sm">
                  Has alcanzado el límite de {FREE_FAVORITES_LIMIT} favoritos gratuitos
                </p>
                <p className="text-amber-800 text-xs mt-1">
                  Activa Premium o un código Beta para guardar favoritos ilimitados.
                </p>
                <Button
                  data-testid="upgrade-from-favorites-btn"
                  onClick={() => { onClose(); navigate('/pricing'); }}
                  size="sm"
                  className="mt-2 bg-amber-500 hover:bg-amber-600 text-white"
                >
                  Ver planes Premium
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#8FBC8F]" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('noFavoritesYet')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {favorites.map((favorite) => (
                <Card key={favorite.barcode} className="relative">
                  <CardContent className="p-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => removeFavorite(favorite.barcode)}
                      data-testid={`remove-favorite-${favorite.barcode}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                    {favorite.image_url && (
                      <img
                        src={favorite.image_url}
                        alt={favorite.product_name}
                        className="w-full h-32 object-contain mb-2"
                      />
                    )}
                    <h3 className="font-semibold text-sm">{favorite.product_name}</h3>
                    {favorite.brands && (
                      <p className="text-xs text-gray-500">{favorite.brands}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{favorite.barcode}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Unlock unlimited favorites button */}
          {!hasUnlimited && !isNearLimit && (
            <div className="mt-6 pt-4 border-t text-center">
              <Button
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
                variant="outline"
                className="border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                data-testid="unlock-favorites-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                {t('unlockUnlimitedFavorites') || 'Guardar productos ilimitados'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};